import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Layout } from "../components/Layout";
import { ImageCapture } from "../components/ImageCapture";
import { Disclaimer } from "../components/Disclaimer";
import { LocationEditor } from "../components/LocationEditor";
import { AlertTriangle, ArrowRight, Calculator } from "lucide-react";
import { toast } from "sonner";

const JOBS = ["Fix", "Repair", "Paint", "Clean", "Flip", "Build", "Haul", "Other"];
const CONDITIONS = ["Good", "Worn", "Broken", "Unknown"];
const WHO = ["Me", "Hire", "Both"];

function Range({ v, currency = "$" }) {
  if (!v) return <span className="font-mono">n/a</span>;
  return <span className="font-mono">{currency}{v.low}–{currency}{v.high}</span>;
}

export default function Measure() {
  const { user, usage, refreshUsage } = useAuth();
  const nav = useNavigate();
  const [b64, setB64] = useState(null);
  const [jobType, setJobType] = useState("Paint");
  const [condition, setCondition] = useState("Worn");
  const [who, setWho] = useState("Me");
  const [busy, setBusy] = useState(false);
  const [est, setEst] = useState(null);
  const [size, setSize] = useState({ width: "", height: "", depth: "" });

  const measureLocked = user?.paid && usage?.applies && !usage.features?.measure?.available;

  const run = async (useSize) => {
    if (!b64) return;
    setBusy(true);
    try {
      const payload = { image_base64: b64, jobType, condition, who };
      if (useSize && (size.width || size.height || size.depth)) payload.userSize = size;
      const { data } = await api.post("/measure", payload);
      setEst(data);
      if (data.measurements) setSize({
        width: data.measurements.width || "", height: data.measurements.height || "", depth: data.measurements.depth || "",
      });
      refreshUsage();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); refreshUsage(); }
    finally { setBusy(false); }
  };

  const makePath = async () => {
    if (!user?.paid) { toast.message("Saving a path is a member feature"); nav("/paywall"); return; }
    try {
      const { data } = await api.post("/sessions", {
        title: `${jobType}: ${est.seen}`, source: "measure",
        summary: `${jobType} job in ${est.place}. Net DIY ~$${est.moneyTheyCouldMake?.netDiy?.low}–$${est.moneyTheyCouldMake?.netDiy?.high}.`,
        pathData: { thisWeek: `Confirm materials cost for this ${jobType.toLowerCase()} job and list one buyer/customer.`, summary: "" },
      });
      toast.success("Path started");
      nav(`/session/${data.id}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const c = est?.costToDo, m = est?.moneyTheyCouldMake;

  return (
    <Layout>
      <div className="fadeup space-y-5">
        <div>
          <h2 className="font-head font-bold text-2xl sm:text-3xl">Measure a thing</h2>
          <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>Cost to do the work vs money you might make in {user?.city || "your place"}.</p>
        </div>

        {!user?.city && <LocationEditor />}

        <ImageCapture onImage={(bb) => { setB64(bb); setEst(null); }} />

        <div className="pm-card p-4 space-y-4">
          <div>
            <div className="label-mono mb-2">Job type</div>
            <div className="flex flex-wrap gap-2" data-testid="job-types">
              {JOBS.map((j) => <button key={j} onClick={() => setJobType(j)} data-testid={`job-${j.toLowerCase()}`}
                className="badge" style={{ background: jobType === j ? "var(--text)" : "transparent", color: jobType === j ? "#fff" : "var(--text)" }}>{j}</button>)}
            </div>
          </div>
          <div>
            <div className="label-mono mb-2">Condition</div>
            <div className="flex flex-wrap gap-2">
              {CONDITIONS.map((cc) => <button key={cc} onClick={() => setCondition(cc)}
                className="badge" style={{ background: condition === cc ? "var(--text)" : "transparent", color: condition === cc ? "#fff" : "var(--text)" }}>{cc}</button>)}
            </div>
          </div>
          <div>
            <div className="label-mono mb-2">Who does the work</div>
            <div className="flex flex-wrap gap-2">
              {WHO.map((w) => <button key={w} onClick={() => setWho(w)} data-testid={`who-${w.toLowerCase()}`}
                className="badge" style={{ background: who === w ? "var(--text)" : "transparent", color: who === w ? "#fff" : "var(--text)" }}>{w}</button>)}
            </div>
          </div>
        </div>

        {b64 && !est && (
          <div className="space-y-2">
            <button onClick={() => run(false)} disabled={busy || measureLocked} className="pm-btn flex items-center gap-2" data-testid="run-measure-btn">
              <Calculator className="w-4 h-4" /> {busy ? "Estimating…" : measureLocked ? "Not enough coins for a measure" : `Estimate cost & earn${user?.paid && usage?.features?.measure ? ` · ${usage.features.measure.cost} coins` : ""}`}
            </button>
            {measureLocked && (
              <p className="text-xs" style={{ color: "var(--warning)" }} data-testid="measure-insufficient-note">A measure costs {usage?.features?.measure?.cost} coins. Buy 50 more at the top of the page or wait for your monthly refresh.</p>
            )}
          </div>
        )}

        {est && (
          <div className="space-y-4" data-testid="measure-result">
            <div className="pm-card p-4">
              <div className="label-mono">Place used: {est.place}</div>
              <div className="mt-1"><b>Seen:</b> {est.seen}</div>
            </div>

            {/* Editable size */}
            <div className="pm-card p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="label-mono">Size — confidence: {est.measurements?.confidence}</div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {["width", "height", "depth"].map((k) => (
                  <div key={k}>
                    <div className="label-mono mb-1">{k}</div>
                    <input className="pm-input font-mono text-sm" value={size[k]} onChange={(e) => setSize({ ...size, [k]: e.target.value })} data-testid={`size-${k}`} />
                  </div>
                ))}
              </div>
              {est.measurements?.area && <div className="text-sm mt-2" style={{ color: "var(--text-2)" }}>{est.measurements.area}</div>}
              <button onClick={() => run(true)} disabled={busy || measureLocked} className="pm-btn-ghost text-sm mt-3" data-testid="recalc-size-btn" title={measureLocked ? "Not enough coins" : ""}>
                {busy ? "Recalculating…" : measureLocked ? "Not enough coins to recalculate" : `I edited size — recalculate${usage?.features?.measure ? ` · ${usage.features.measure.cost} coins` : ""}`}
              </button>
            </div>

            {/* Cost & money */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="pm-card p-4">
                <div className="label-mono mb-2" style={{ color: "var(--cost)" }}>Cost to do</div>
                <Row label="Materials" v={<Range v={c?.materials} />} />
                <Row label="Tools" v={<Range v={c?.tools} />} note={c?.tools?.note} />
                {who !== "Me" && <Row label="Hired labor" v={<Range v={c?.hiredLabor} />} />}
                <Row label="DIY hours" v={<Range v={c?.diyHours} currency="" />} />
                <Row label="Cash out (DIY)" v={<Range v={c?.cashOutDiy} />} strong />
              </div>
              <div className="pm-card p-4">
                <div className="label-mono mb-2" style={{ color: "var(--earn)" }}>Money you might make</div>
                <Row label="As-is sale" v={<Range v={m?.asIs} />} />
                <Row label="After work" v={<Range v={m?.afterWork} />} />
                {m?.chargeIfService && <Row label="Charge for job" v={<Range v={m?.chargeIfService} />} />}
                <Row label="Net (DIY)" v={<Range v={m?.netDiy} />} strong />
                <Row label="Implied hourly" v={<Range v={m?.impliedHourlyDiy} />} />
              </div>
            </div>

            {est.waste?.likely && (
              <div className="pm-card p-4" style={{ borderColor: "var(--warning)" }} data-testid="measure-waste">
                <div className="label-mono flex items-center gap-2" style={{ color: "var(--warning)" }}><AlertTriangle className="w-4 h-4" /> Likely a waste</div>
                <p className="text-sm mt-1">{est.waste.why}</p>
              </div>
            )}

            {est.assumptions?.length > 0 && (
              <div className="pm-card p-4">
                <div className="label-mono mb-2">Assumptions</div>
                <ul className="text-sm space-y-1" style={{ color: "var(--text-2)" }}>
                  {est.assumptions.map((a, i) => <li key={i}>• {a}</li>)}
                </ul>
              </div>
            )}

            <p className="text-xs font-mono" style={{ color: "var(--text-2)" }}>{est.disclaimer} For {est.place}.</p>

            <button onClick={makePath} className="pm-btn flex items-center gap-2" data-testid="measure-makepath-btn">
              Make this my path <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
        <Disclaimer compact />
      </div>
    </Layout>
  );
}

function Row({ label, v, note, strong }) {
  return (
    <div className="flex items-center justify-between py-1 text-sm" style={strong ? { borderTop: "1px solid var(--border)", marginTop: 4, paddingTop: 6, fontWeight: 700 } : {}}>
      <span>{label}{note && <span className="block label-mono normal-case" style={{ letterSpacing: 0 }}>{note}</span>}</span>
      <span>{v}</span>
    </div>
  );
}
